import React, { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, MessageSquare, Pencil, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../../components/Sidebar';
import { cn } from '../../lib/utils';
import ChatPanel from './ChatPanel';
import SurveyCanvas from './SurveyCanvas';
import { useSurveyCreator } from './useSurveyCreator';

const SurveyCreatorPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { sessionId: sessionIdParam } = useParams<{ sessionId?: string }>();
  const initialSessionId = sessionIdParam ? Number(sessionIdParam) : undefined;

  const [mobilePane, setMobilePane] = useState<'chat' | 'canvas'>('canvas');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  const creator = useSurveyCreator({
    initialSessionId,
    enabled: isAdmin,
    onSessionChange: (id) =>
      navigate(id ? `/survey-creator/${id}` : '/survey-creator', { replace: true }),
    onSurveyCreated: (surveyId) => navigate(`/surveys/${surveyId}`),
  });

  // Admin-only feature (V0).
  if (!isAdmin) return <Navigate to="/" replace />;

  const { canvasOpen } = creator;

  const commitRename = () => {
    if (editingId != null && editingTitle.trim())
      creator.renameSession(editingId, editingTitle.trim());
    setEditingId(null);
  };

  return (
    <div className="h-screen bg-[#FEFEFE] flex overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col md:ml-16 overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-[#F1F5F9] px-6 py-3 mt-16 md:mt-0 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 text-[#6B7280] hover:text-[#374151] hover:bg-[#FAFBFC] rounded-lg transition-colors"
                title="Back to Home"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-9 h-9 rounded-full bg-[#07E5D1] flex items-center justify-center text-white">
                <Sparkles className="w-4.5 h-4.5" />
              </div>
              <div>
                <h1 className="font-semibold text-[#1C1C1E] text-sm">Survey Creation Agent</h1>
                <p className="text-xs text-[#9CA3AF]">Draft, refine, and save a survey with AI</p>
              </div>
            </div>
            {canvasOpen && (
              <div className="md:hidden flex rounded-lg border border-[#E5E7EB] overflow-hidden text-xs">
                <button
                  onClick={() => setMobilePane('chat')}
                  className={cn(
                    'px-3 py-1.5',
                    mobilePane === 'chat' ? 'bg-[#07E5D1] text-white' : 'text-gray-500',
                  )}
                >
                  Chat
                </button>
                <button
                  onClick={() => setMobilePane('canvas')}
                  className={cn(
                    'px-3 py-1.5',
                    mobilePane === 'canvas' ? 'bg-[#07E5D1] text-white' : 'text-gray-500',
                  )}
                >
                  Draft
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Body: session list (left) + chat + canvas */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left session-history pane (consistent with the other chat pages) */}
          <aside className="hidden md:flex w-56 bg-white border-r border-[#F1F5F9] flex-col flex-shrink-0">
            <div className="p-3 border-b border-[#F1F5F9]">
              <button
                onClick={() => creator.newSession()}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white bg-[#07E5D1] hover:bg-[#06D4C2] transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> New survey
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {creator.sessions.map((s) => (
                <div
                  key={s.id}
                  className={cn(
                    'group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors text-xs',
                    s.id === creator.sessionId
                      ? 'bg-[#E8FCFA] text-[#07E5D1]'
                      : 'text-[#374151] hover:bg-[#FAFBFC]',
                  )}
                  onClick={() => editingId !== s.id && creator.openSession(s.id)}
                >
                  <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
                  {editingId === s.id ? (
                    <div
                      className="flex-1 flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename();
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        autoFocus
                        className="flex-1 px-1.5 py-0.5 text-xs bg-white border border-[#07E5D1] rounded focus:outline-none text-[#374151]"
                      />
                      <button onClick={commitRename} className="p-0.5 text-[#10B981]">
                        <Check className="w-3 h-3" />
                      </button>
                      <button onClick={() => setEditingId(null)} className="p-0.5 text-[#9CA3AF]">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="flex-1 truncate">{s.title || `Survey ${s.id}`}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingId(s.id);
                          setEditingTitle(s.title || '');
                        }}
                        className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-[#07E5D1] transition-all"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          creator.removeSession(s.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-[#EF4444] transition-all"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              ))}
              {creator.sessions.length === 0 && (
                <p className="text-xs text-[#9CA3AF] text-center py-8">No surveys yet</p>
              )}
            </div>
          </aside>

          {/* Chat */}
          <div
            className={cn(
              'flex-col min-w-0 transition-all duration-500 ease-in-out',
              canvasOpen ? 'md:w-[42%] md:border-r md:border-[#F1F5F9]' : 'flex-1',
              canvasOpen && mobilePane === 'canvas' ? 'hidden md:flex' : 'flex w-full md:w-auto',
            )}
          >
            <ChatPanel
              messages={creator.messages}
              isLoading={creator.isLoading}
              progress={creator.progress}
              error={creator.error}
              onSend={creator.sendMessage}
            />
          </div>

          {/* Canvas */}
          {canvasOpen && (
            <div
              className={cn(
                'flex-1 min-w-0 transition-all duration-500 ease-in-out',
                mobilePane === 'chat' ? 'hidden md:block' : 'block',
              )}
            >
              <SurveyCanvas
                key={creator.sessionId ?? 'new'}
                draftMarkdown={creator.draftMarkdown}
                draftVersion={creator.versionIdx + 1}
                isEditing={creator.isEditing}
                isSaving={creator.isSaving}
                createdSurveyId={creator.createdSurveyId}
                canUndo={creator.canUndo}
                canRedo={creator.canRedo}
                showEditedFlash={!!creator.lastEditedRange}
                onApplyEdit={creator.applyHighlightEdit}
                onManualChange={creator.setManualDraft}
                onUndo={creator.undo}
                onRedo={creator.redo}
                onSave={creator.saveSurvey}
                onViewSurvey={(id) => navigate(`/surveys/${id}`)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyCreatorPage;
